import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { 
  FilterState, 
  FilterActions, 
  DateRange, 
  DayOfWeekFilter, 
  TimeFilter, 
  DiningBehaviorFilter,
  FALLBACK_FILTER_STATE,
  createDefaultFilterState
} from '../types/filters';
import { getSystemLatestBusinessDate, getStoreLatestBusinessDate } from '../services/api';

interface FilterContextType extends FilterState, FilterActions {
  isLoadingDefaultDates: boolean;
  activeHours: number[]; 
  tempFilters: FilterState;
  setTempFilters: (filters: FilterState) => void;
  applyTempFilters: () => void;
  cancelTempFilters: () => void;
  hasUnappliedChanges: boolean;
  isFiltersApplying: boolean; 
}

const FilterContext = createContext<FilterContextType | undefined>(undefined);

interface FilterProviderProps {
  children: ReactNode;
}

const getStoreIdFromURL = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('storeId');
};

const parseFiltersFromURL = (defaultState: FilterState): FilterState => {
  const urlParams = new URLSearchParams(window.location.search);
  
  const startDate = urlParams.get('startDate') || defaultState.dateRange.startDate;
  const urlEndDate = urlParams.get('endDate');
  
  
  const endDate = (urlEndDate && urlEndDate <= defaultState.dateRange.endDate) 
    ? urlEndDate 
    : defaultState.dateRange.endDate;
  
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  const validStartDate = dateRegex.test(startDate) ? startDate : defaultState.dateRange.startDate;
  const validEndDate = dateRegex.test(endDate) ? endDate : defaultState.dateRange.endDate;
  
  const daysParam = urlParams.get('days');
  const selectedDays = daysParam ? daysParam.split(',').map(Number) : defaultState.dayOfWeek.selectedDays;
  const allDays = urlParams.get('allDays') === 'true' || (!daysParam && defaultState.dayOfWeek.allDays);
  
  const hoursParam = urlParams.get('hours');
  const selectedHours = hoursParam ? hoursParam.split(',').map(Number) : defaultState.time.selectedHours;
  const allHours = urlParams.get('allHours') === 'true' || (!hoursParam && defaultState.time.allHours);
  
  const timePeriods = { ...defaultState.time.timePeriods };
  
  const behaviorsParam = urlParams.get('behaviors');
  const selectedBehaviors = behaviorsParam ? behaviorsParam.split(',') : defaultState.diningBehavior.selectedBehaviors;
  const allBehaviors = urlParams.get('allBehaviors') === 'true' || (!behaviorsParam && defaultState.diningBehavior.allBehaviors);
  
  return {
    dateRange: { startDate: validStartDate, endDate: validEndDate },
    dayOfWeek: { selectedDays, allDays },
    time: { selectedHours, allHours, timePeriods },
    diningBehavior: { selectedBehaviors, allBehaviors }
  };
};


const updateURLFromFilters = (filters: FilterState) => {
  const params = new URLSearchParams();
  
  const currentParams = new URLSearchParams(window.location.search);
  let lang = currentParams.get('lang');

  if (!lang) {
    lang = 'ko'; 
  }
  params.set('lang', lang);

  const currentStoreId = getStoreIdFromURL();
  if (currentStoreId) {
    params.set('storeId', currentStoreId);
  }
  
  params.set('startDate', filters.dateRange.startDate);
  params.set('endDate', filters.dateRange.endDate);
  
  if (!filters.dayOfWeek.allDays && filters.dayOfWeek.selectedDays.length > 0) {
    params.set('days', filters.dayOfWeek.selectedDays.join(','));
  }
  params.set('allDays', filters.dayOfWeek.allDays.toString());
  
  if (!filters.time.allHours && filters.time.selectedHours.length > 0) {
    params.set('hours', filters.time.selectedHours.join(','));
  }
  params.set('allHours', filters.time.allHours.toString());
  
  if (!filters.diningBehavior.allBehaviors && filters.diningBehavior.selectedBehaviors.length > 0) {
    params.set('behaviors', filters.diningBehavior.selectedBehaviors.join(','));
  }
  params.set('allBehaviors', filters.diningBehavior.allBehaviors.toString());
  
  
  
  const newUrl = `${window.location.pathname}?${params.toString()}`;
  window.history.replaceState({}, '', newUrl);
};


export const FilterProvider: React.FC<FilterProviderProps> = ({ children }) => {
    
    const [filterState, setFilterState] = useState<FilterState>(FALLBACK_FILTER_STATE);
    const [tempFilters, setTempFilters] = useState<FilterState>(FALLBACK_FILTER_STATE);
    const [isLoadingDefaultDates, setIsLoadingDefaultDates] = useState<boolean>(true);
    const [activeHours, setActiveHours] = useState<number[]>(Array.from({ length: 24 }, (_, i) => i));
    const [isFiltersApplying, setIsFiltersApplying] = useState<boolean>(false);
  
  
    useEffect(() => {
      const loadDefaultDates = async () => {
        try {
          setIsLoadingDefaultDates(true);
          
          const storeId = getStoreIdFromURL();
          let dateData;
          
          if (storeId) {
            dateData = await getStoreLatestBusinessDate(storeId);
          } else {
            dateData = await getSystemLatestBusinessDate();
          }
          
          const defaultState = createDefaultFilterState(dateData);
          const finalState = parseFiltersFromURL(defaultState);
          
          setFilterState(finalState);
          setTempFilters(finalState); 
          
          if (storeId) {
            try {
              
              
              const fallbackOperatingHours = Array.from({length: 24}, (_, i) => i);
              setActiveHours(fallbackOperatingHours);
              
              
              const timeFilterWithOperatingHours = {
                selectedHours: fallbackOperatingHours, 
                allHours: true, 
                timePeriods: {} 
              };
              
              setFilterState(prev => ({
                ...prev,
                time: timeFilterWithOperatingHours
              }));
              
              setTempFilters(prev => ({
                ...prev,
                time: timeFilterWithOperatingHours
              }));
            } catch (hourlyError) {
              console.warn('Failed to set operating hours, using 24-hour fallback:', hourlyError);
            }
          }
        } catch (error) {
          console.error('Failed to load default dates, using fallback:', error);
          const fallbackState = parseFiltersFromURL(FALLBACK_FILTER_STATE);
          setFilterState(fallbackState);
          setTempFilters(fallbackState);
        } finally {
          setIsLoadingDefaultDates(false);
        }
      };
  
      loadDefaultDates();
    }, []); 
  
    useEffect(() => {
      if (!isLoadingDefaultDates) {
        updateURLFromFilters(filterState);
      }
    }, [filterState, isLoadingDefaultDates]);
  
    const applyTempFilters = useCallback(async () => {
      setIsFiltersApplying(true);
      
      try {
        setFilterState(tempFilters);
        await new Promise(resolve => setTimeout(resolve, 150));
      } catch (error) {
        console.error('❌ Error applying filters:', error);
      } finally {
        setIsFiltersApplying(false);
      }
    }, [tempFilters]);
  
    const cancelTempFilters = useCallback(() => {
      setTempFilters(filterState);
    }, [filterState]);
  
    const hasUnappliedChanges = JSON.stringify(filterState) !== JSON.stringify(tempFilters);
  
    const setDateRange = useCallback((dateRange: DateRange) => {
      setTempFilters(prev => ({ ...prev, dateRange }));
    }, []);
  
    const setDayOfWeekFilter = useCallback((filter: DayOfWeekFilter) => {
      setTempFilters(prev => ({ ...prev, dayOfWeek: filter }));
    }, []);
  
    const setTimeFilter = useCallback((filter: TimeFilter) => {
      setTempFilters(prev => ({ ...prev, time: filter }));
    }, []);
  
    const setDiningBehaviorFilter = useCallback((filter: DiningBehaviorFilter) => {
      setTempFilters(prev => ({ ...prev, diningBehavior: filter }));
    }, []);
  
    
    
    
    
  
    const resetFilters = useCallback(async () => {
      try {
        setIsLoadingDefaultDates(true);
        const storeId = getStoreIdFromURL();
        let dateData;
        if (storeId) {
          dateData = await getStoreLatestBusinessDate(storeId);
        } else {
          dateData = await getSystemLatestBusinessDate();
        }
        const defaultState = createDefaultFilterState(dateData);
        setFilterState(defaultState);
        setTempFilters(defaultState);
      } catch (error) {
        console.error('Failed to reset filters with latest dates:', error);
        setFilterState(FALLBACK_FILTER_STATE);
        setTempFilters(FALLBACK_FILTER_STATE);
      } finally {
        setIsLoadingDefaultDates(false);
      }
    }, []);
  
    const contextValue: FilterContextType = {
      ...filterState,
      isLoadingDefaultDates,
      activeHours,
      tempFilters,
      setTempFilters,
      applyTempFilters,
      cancelTempFilters,
      hasUnappliedChanges,
      isFiltersApplying, 
      setDateRange,
      setDayOfWeekFilter,
      setTimeFilter,
      setDiningBehaviorFilter,
      resetFilters
    };
  
    return (
      <FilterContext.Provider value={contextValue}>
        {children}
      </FilterContext.Provider>
    );
  };
  
  export const useFilters = (): FilterContextType => {
    const context = useContext(FilterContext);
    if (context === undefined) {
      throw new Error('useFilters must be used within a FilterProvider');
    }
    return context;
  };